<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui'
import type { Organization } from '@/stores/admin'
import { slugify } from '@/lib/utils'
import { computed, reactive, ref, watch } from 'vue'
import { organizationFormSchema } from '@/views/forms/schema.ts'
import type { OrganizationFormSchema } from '@/views/forms/schema.ts'

const props = defineProps<{
  organization?: Organization
  onSubmit: (event: FormSubmitEvent<OrganizationFormSchema & { delete_logo?: boolean }>) => void
  submitButtonText: string
}>()

const formState = reactive<Partial<OrganizationFormSchema>>({
  name: props.organization?.name,
  slug: props.organization?.slug,
  client_name: props.organization?.client_name ?? undefined,
  logo_file: undefined,
})

const pendingLogoDelete = ref(false)

// Reset form state when organization prop changes (e.g., after save)
watch(
  () => props.organization,
  (newOrg) => {
    formState.name = newOrg?.name
    formState.slug = newOrg?.slug
    formState.client_name = newOrg?.client_name ?? undefined
    formState.logo_file = undefined
    pendingLogoDelete.value = false
  },
  { immediate: true },
)

// Watch for changes in the name field and auto-generate slug (only for new orgs)
watch(
  () => formState.name,
  (newName) => {
    if (!props.organization && newName) {
      formState.slug = slugify(newName)
    }
  },
)

// Clear delete intent when new file is selected
watch(
  () => formState.logo_file,
  (newFile) => {
    if (newFile) pendingLogoDelete.value = false
  },
)

const hasChanges = computed(() => {
  if (!props.organization) return !!formState.name // new org: has changes if name entered
  return (
    formState.name !== props.organization.name ||
    formState.slug !== props.organization.slug ||
    (formState.client_name ?? '').trim() !== (props.organization.client_name ?? '') ||
    !!formState.logo_file ||
    pendingLogoDelete.value
  )
})

function handleSubmit(event: FormSubmitEvent<OrganizationFormSchema>) {
  props.onSubmit({
    ...event,
    data: {
      ...event.data,
      delete_logo: pendingLogoDelete.value,
    },
  } as FormSubmitEvent<OrganizationFormSchema & { delete_logo?: boolean }>)
}
</script>

<template>
  <UForm
    :schema="organizationFormSchema"
    :state="formState"
    class="space-y-4"
    @submit="handleSubmit"
  >
    <UFormField
      label="Name"
      name="name"
      description="The organization name will appear in the user interface."
      class="flex max-sm:flex-col justify-between items-start gap-4"
    >
      <UInput v-model="formState.name" />
    </UFormField>
    <USeparator />
    <UFormField
      label="Slug"
      name="slug"
      description="A unique identifier for the organization, it's usually used in URLs."
      class="flex max-sm:flex-col justify-between items-start gap-4"
    >
      <UInput v-model="formState.slug" />
    </UFormField>
    <USeparator />
    <UFormField
      label="Client name"
      name="client_name"
      description="Data platform client identifier (must match dbt prod_client_specs)."
      class="flex max-sm:flex-col justify-between items-start gap-4"
    >
      <UInput v-model="formState.client_name" placeholder="e.g. Dior, Brut" />
    </UFormField>
    <USeparator />
    <UFormField
      label="Logo"
      name="logo_file"
      class="flex max-sm:flex-col justify-between items-start gap-4"
    >
      <UFileUpload v-model="formState.logo_file" accept="image/*" />
      <template #description>
        Pick a square logo without background, in png or jpeg format.
        <br />
        <br />
        <div v-if="organization?.logo_url && !pendingLogoDelete" class="flex items-center gap-2">
          <span>Current logo:</span>
          <UAvatar :src="organization.logo_url" alt="Organization logo" />
          <UButton
            icon="i-lucide-trash-2"
            color="error"
            variant="ghost"
            size="xs"
            @click="pendingLogoDelete = true"
          />
        </div>
        <div v-else-if="pendingLogoDelete" class="flex items-center gap-2">
          <span class="text-error">Logo will be deleted</span>
          <UButton
            icon="i-lucide-undo"
            color="neutral"
            variant="ghost"
            size="xs"
            @click="pendingLogoDelete = false"
          >
            Undo
          </UButton>
        </div>
      </template>
    </UFormField>
    <UButton
      type="submit"
      color="primary"
      loading-auto
      :variant="hasChanges ? 'solid' : 'soft'"
      :disabled="!hasChanges"
      :class="{ 'animate-pulse': hasChanges }"
    >
      {{ submitButtonText }}
    </UButton>
  </UForm>
</template>
